import { useEffect, useState } from 'react';
import { Routes, Route, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAppDispatch } from './store/hooks';
import ProtectedRoute from './components/ProtectedRoute';
import AppHeader from './components/AppHeader';
import DashboardScreen from './pages/Dashboard/Dashboard';
import BookPage from './pages/Book/Book';
import SignupInfo from './pages/SignupInfo/SignupInfo';
import NotionEditor from './pages/NotionEditor/NotionEditor';
import Profile from './pages/Profile/Profile';
import Login from './pages/Login/Login';
import AdminDashboard from './pages/AdminDashboard/AdminDashboard';
import BookDetails from './pages/AdminDashboard/BookDetails';
import SupportDashboard from './pages/AdminDashboard/SupportDashboard';
import AdminLogin from './pages/AdminDashboard/AdminLogin';
import UserDirectory from './pages/AdminDashboard/UserDirectory';
import OutreachDashboard from './pages/AdminDashboard/OutreachDashboard';
import AnalyticsDashboard from './pages/AdminDashboard/AnalyticsDashboard';
import FeedbackPage from './pages/AdminDashboard/FeedbackPage';
import AdminProtectedRoute from './components/AdminProtectedRoute';
import SalesProtectedRoute from './components/SalesProtectedRoute';
import SuperAdminProtectedRoute from './components/SuperAdminProtectedRoute';
import SalesDashboard from './pages/SalesDashboard/SalesDashboard';
import StaffManagement from './pages/AdminDashboard/StaffManagement';
import VerifyEmail from './pages/VerifyEmail/VerifyEmail';
import InterceptorSetup from './api/interceptor';
import { endpoints, baseUrl } from './api/api';
import { loginSuccess, logout } from './store/slices/authslice';
import { store } from './store/store';
import { trackPageView, identifyUser } from './utils/analytics';
import SupportChatWidget from './components/SupportChatWidget';
import CoverDesigner from './pages/CoverDesigner';
import TermsModal from './pages/Login/components/TermsModal';

InterceptorSetup()

function App() {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const [showTerms, setShowTerms] = useState(false);

  // Registered once on mount — stable listener never misses the terms:required event
  useEffect(() => {
    const handler = () => setShowTerms(true);
    window.addEventListener('terms:required', handler);
    return () => window.removeEventListener('terms:required', handler);
  }, []);

  const handleTermsAccept = async () => {
    const token = localStorage.getItem('accessToken');
    try {
      await axios.post(`${baseUrl}${endpoints.acceptTerms}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (e) {}
    setShowTerms(false);
    window.location.reload();
  };

  const handleTermsDecline = () => {
    const token = localStorage.getItem('accessToken');
    axios.post(`${baseUrl}${endpoints.declineTerms}`, {}, {
      headers: { Authorization: `Bearer ${token}` }
    }).catch(() => {}).finally(() => {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      store.dispatch(logout());
      setShowTerms(false);
      navigate('/login');
    });
  };

  // Track page views on route change — skip admin pages to avoid polluting user metrics
  useEffect(() => {
    if (!location.pathname.startsWith('/admin')) {
      trackPageView(location.pathname);
    }
  }, [location]);

  // Fetch authenticated user on app load
  useEffect(() => {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) {
      axios.get(endpoints.getMe)
        .then((response) => {
          const { user } = response.data;
          dispatch(loginSuccess({
            id: user._id,
            name: user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'User',
            mobile: user.phoneNumber || '',
            email: user.email || '',
            emailVerified: user.emailVerified,
            role: user.role,
            firstName: user.firstName,
            lastName: user.lastName,
            profileImage: user.profileImage,
            profileComplete: user.profileComplete,
            phoneVerified: user.phoneVerified,
          }));
          // email/name intentionally excluded — Amplitude's userId already maps
          // 1:1 to this Mongo _id, so anyone needing the real identity can look
          // it up there instead of duplicating PII into a third-party tool.
          identifyUser(user._id, {
            email_verified: user.emailVerified,
            phone_verified: user.phoneVerified,
            role: user.role,
            profile_complete: user.profileComplete,
          });
          // Keep localStorage in sync with fresh server data
          localStorage.setItem('user', JSON.stringify(user));
        })
        .catch(() => {
          // Interceptor handles 401 refresh/logout automatically
        });
    }
  }, [dispatch]);

  const showAppHeader = !['/login', '/signup-info', '/verify-email', '/cover-designer'].includes(location.pathname) && !location.pathname.startsWith('/admin') && !location.pathname.startsWith('/sales');
  const showSupportWidget = !['/login', '/signup-info', '/verify-email', '/admin/login', '/cover-designer'].includes(location.pathname) && !location.pathname.startsWith('/admin') && !location.pathname.startsWith('/sales');

  return (
    <div className="font-sans">
      {showTerms && <TermsModal onAccept={handleTermsAccept} onDecline={handleTermsDecline} />}
      {showAppHeader && <AppHeader />}
      {showSupportWidget && <SupportChatWidget />}

      <Routes>
        <Route path="/" element={
          <ProtectedRoute>
            <DashboardScreen />
          </ProtectedRoute>
        } />
        <Route path="/signup-info" element={<SignupInfo />} />
        <Route path="/books" element={
          <ProtectedRoute>
            <BookPage />
          </ProtectedRoute>
        } />
        <Route path="/notion" element={
          <ProtectedRoute>
            <NotionEditor />
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        } />
        <Route path="/admin/login" element={<AdminLogin />} />
        <Route path="/admin" element={
          <AdminProtectedRoute>
            <AdminDashboard />
          </AdminProtectedRoute>
        } />
        <Route path="/admin/book/:id" element={
          <AdminProtectedRoute>
            <BookDetails />
          </AdminProtectedRoute>
        } />
        <Route path="/admin/users" element={
          <AdminProtectedRoute>
            <UserDirectory />
          </AdminProtectedRoute>
        } />
        <Route path="/admin/outreach" element={
          <AdminProtectedRoute>
            <OutreachDashboard />
          </AdminProtectedRoute>
        } />
        <Route path="/admin/analytics" element={
          <AdminProtectedRoute>
            <AnalyticsDashboard />
          </AdminProtectedRoute>
        } />
        <Route path="/admin/support" element={
          <AdminProtectedRoute>
            <SupportDashboard />
          </AdminProtectedRoute>
        } />
        <Route path="/admin/feedback" element={
          <AdminProtectedRoute>
            <FeedbackPage />
          </AdminProtectedRoute>
        } />
        <Route path="/sales" element={
          <SalesProtectedRoute>
            <SalesDashboard />
          </SalesProtectedRoute>
        } />
        <Route path="/admin/staff" element={
          <SuperAdminProtectedRoute>
            <StaffManagement />
          </SuperAdminProtectedRoute>
        } />
        <Route path="/cover-designer" element={
          <ProtectedRoute>
            <CoverDesigner />
          </ProtectedRoute>
        } />
        <Route path="/login" element={<Login />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
      </Routes>
    </div>
  );
}

export default App;
